import React from "react";
import { Navigate } from "react-router-dom";

// =====================================================
// NORMAL APPLICATION
// =====================================================

import Login from "../pages/Authentication/Login";
import Dashboard from "../pages/Dashboard/Dashboard";

// Leads
import AllLeads from "../pages/Leads/AllLeads";
import LeadDetails from "../pages/Leads/LeadDetails";
import NewLeads from "../pages/Leads/NewLeads";
import FollowUps from "../pages/Leads/FollowUps";
import Counselling from "../pages/Leads/Counselling";

// Marketing
import Campaigns from "../pages/Campaigns/Campaigns";
import CampaignDetails from "../pages/Campaigns/CampaignDetails";
import LeadSources from "../pages/LeadSources/LeadSources";
import Analytics from "../pages/Analytics/Analytics";
import CampaignCreatePage from "../pages/Campaigns/CampaignCreatePage";
import MarketingAssetsPage from "../pages/Campaigns/MarketingAssetsPage";

// Communications
import WhatsApp from "../pages/WhatsApp/WhatsApp";
import Conversations from "../pages/Conversations/Conversations";

// Admissions
import Applications from "../pages/Admissions/Applications";
import ApplicationDetails from "../pages/Admissions/ApplicationDetails";
import Admissions from "../pages/Admissions/Admissions";

// Integrations
import MetaIntegration from "../pages/Integrations/Meta";
import GoogleIntegration from "../pages/Integrations/Google";
import YouTubeIntegration from "../pages/Integrations/YouTube";
import WhatsAppIntegration from "../pages/Integrations/WhatsApp";

// Settings
import OrganizationSettings from "../pages/Settings/Organization";
import UsersSettings from "../pages/Settings/Users";
import StatusSettings from "../pages/Settings/Statuses";
import PublicLeadSettings from "../pages/Settings/PublicLeads";
import AuditLogs from "../pages/Settings/AuditLogs";

// Profile
import Profile from "../pages/Profile/Profile";

// =====================================================
// SUPERADMIN
// =====================================================

import SuperAdminLogin from "../pages/Authentication/Login";
import SuperAdminDashboard from "../pages/Dashboard/Dashboard";

const SUPERADMIN_TOKEN_KEY = "superadminToken";

// =====================================================
// AUTH HELPERS
// =====================================================

const isAuthenticated = () => {
  return !!localStorage.getItem("token");
};

const isSuperAdminAuthenticated = () => {
  return !!localStorage.getItem(SUPERADMIN_TOKEN_KEY);
};

// =====================================================
// SUPERADMIN GUARD
// IMPORTANT:
// This guard is placed inside publicRoutes so the normal
// application's "token" auth wrapper cannot block it.
// =====================================================

const SuperAdminGuard = ({ children }) => {
  return isSuperAdminAuthenticated() ? (
    children
  ) : (
    <Navigate
      to="/superadmin/login"
      replace
    />
  );
};

// =====================================================
// NORMAL AUTH PROTECTED ROUTES
// =====================================================

const authProtectedRoutes = [
  // Dashboard
  {
    path: "/dashboard",
    component: <Dashboard />,
  },

  // Leads
  {
    path: "/leads",
    component: <AllLeads />,
  },
  {
    path: "/leads/:id",
    component: <LeadDetails />,
  },
  {
    path: "/leads/new",
    component: <NewLeads />,
  },
  {
    path: "/leads/follow-ups",
    component: <FollowUps />,
  },
  {
    path: "/leads/counselling",
    component: <Counselling />,
  },

  // Marketing
  {
    path: "/campaigns",
    component: <Campaigns />,
  },
  {
    path: "/campaigns/create",
    component: <CampaignCreatePage />,
  },
  {
    path: "/campaigns/:id/edit",
    component: <CampaignCreatePage />,
  },
  {
    path: "/campaigns/:id",
    component: <CampaignDetails />,
  },
  {
    path: "/lead-sources",
    component: <LeadSources />,
  },
  {
    path: "/analytics",
    component: <Analytics />,
  },
  {
    path: "/marketing/assets",
    component: <MarketingAssetsPage />,
  },

  // Communications
  {
    path: "/whatsapp",
    component: <WhatsApp />,
  },
  {
    path: "/conversations",
    component: <Conversations />,
  },

  // Admissions
  {
    path: "/applications",
    component: <Applications />,
  },
  {
    path: "/applications/:id",
    component: <ApplicationDetails />,
  },
  {
    path: "/admissions",
    component: <Admissions />,
  },

  // Integrations
  {
    path: "/integrations/meta",
    component: <MetaIntegration />,
  },
  {
    path: "/integrations/google",
    component: <GoogleIntegration />,
  },
  {
    path: "/integrations/youtube",
    component: <YouTubeIntegration />,
  },
  {
    path: "/integrations/whatsapp",
    component: <WhatsAppIntegration />,
  },

  // Settings
  {
    path: "/settings/organization",
    component: <OrganizationSettings />,
  },
  {
    path: "/settings/users",
    component: <UsersSettings />,
  },
  {
    path: "/settings/statuses",
    component: <StatusSettings />,
  },
  {
    path: "/settings/public-leads",
    component: <PublicLeadSettings />,
  },
  {
    path: "/settings/audit-logs",
    component: <AuditLogs />,
  },

  // Profile
  {
    path: "/profile",
    component: <Profile />,
  },
];

// =====================================================
// PUBLIC ROUTES
// =====================================================

const publicRoutes = [
  // Normal application login
  {
    path: "/login",
    component: <Login />,
  },

  // Superadmin login
  {
    path: "/superadmin/login",
    component: <SuperAdminLogin />,
  },

  // Superadmin dashboard
  //
  // This is intentionally inside publicRoutes with its own
  // guard. Do NOT put it under the normal authProtectedRoutes
  // because that route expects localStorage "token".
  {
    path: "/superadmin/dashboard",
    component: (
      <SuperAdminGuard>
        <SuperAdminDashboard />
      </SuperAdminGuard>
    ),
  },
];

// =====================================================
// DEFAULT ROUTE
// =====================================================

const rootRoute = {
  path: "/",
  component: isAuthenticated() ? (
    <Navigate
      to="/dashboard"
      replace
    />
  ) : isSuperAdminAuthenticated() ? (
    <Navigate
      to="/superadmin/dashboard"
      replace
    />
  ) : (
    <Navigate
      to="/login"
      replace
    />
  ),
};

const notFoundRoute = {
  path: "*",
  component: isAuthenticated() ? (
    <Navigate
      to="/dashboard"
      replace
    />
  ) : isSuperAdminAuthenticated() ? (
    <Navigate
      to="/superadmin/dashboard"
      replace
    />
  ) : (
    <Navigate
      to="/login"
      replace
    />
  ),
};

authProtectedRoutes.push(rootRoute);
authProtectedRoutes.push(notFoundRoute);

// =====================================================
// EXPORT
// =====================================================

export {
  authProtectedRoutes,
  publicRoutes,
};
